import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class LazyLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          p={3}
        >
          <Alert 
            severity="error" 
            sx={{ mb: 2, maxWidth: '500px' }}
          >
            <Typography variant="h6" gutterBottom>
              Failed to load component
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              There was an error loading this page. This might be due to a 
              network issue or a problem with the component.
            </Typography>
          </Alert>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={this.handleRetry}
            sx={{ mt: 1 }}
          >
            Try Again
          </Button>
          
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1} maxWidth="600px">
              <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem' }}>
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default LazyLoadErrorBoundary;